/**
 * The KPI window, opened from the quotation edit screen and shown in a demo.
 *
 * One page, five numbers, one closing line. Deliberately not a report: it is
 * projected in a room, so the whole thing has to be readable from the back of
 * it and understood without being talked through.
 *
 * The five are fixed, not "the top five by size". A hospital owner recognises
 * no-shows, beds, lab, pharmacy and billing as the five places their money
 * lives, and the same five appearing on every quote is what makes the screen
 * comparable between hospitals. A metric that cannot be priced keeps its card
 * and says what it is waiting on, rather than being quietly swapped for
 * whichever driver happened to be bigger — the shape of the story stays the
 * same hospital to hospital.
 *
 * Each card answers three questions in the order a customer asks them:
 *   what is this part of my hospital earning me today
 *   how much of it am I missing
 *   how much does eMedHub save me, and how
 *
 * All three come straight from the audit payload — basis_metric, annual_leak
 * and recoverable — so nothing on this page is computed in the browser.
 *
 * The plan price sits beside the savings on purpose. A recovery figure on its
 * own is a claim; the same figure next to what the plan costs is a comparison
 * the customer makes for themselves, which is the only kind they believe.
 *
 * The three volume inputs stay editable. The moment a hospital says "we do 400
 * OP a day, not 200", the rep types it and every number moves while they watch.
 * That is the whole reason this is a screen and not a PDF.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RefreshCw, AlertTriangle, Check } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { QUOTATION_KPI_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

/** Indian money at demo scale: crore and lakh, because ₹2,71,29,271 cannot be read aloud. */
const money = (n) => {
    const v = Math.abs(Number(n) || 0);
    if (v >= 1e7) return `₹${(v / 1e7).toFixed(v >= 1e8 ? 0 : 2)} Cr`;
    if (v >= 1e5) return `₹${(v / 1e5).toFixed(v >= 1e6 ? 0 : 1)} L`;
    if (v >= 1e3) return `₹${Math.round(v / 1e3)}K`;
    return `₹${Math.round(v)}`;
};

const num = (n) => (Number(n) || 0).toLocaleString("en-IN");

/** Exact rupees, for the one figure on this page that is a price rather than an
 *  estimate. money() rounds ₹73,744 to "₹74K", which is the right call for a
 *  leak nobody can measure to the rupee and the wrong one for what the customer
 *  is being asked to pay. */
const rupees = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

/** How long the plan takes to pay for itself out of what it stops.
 *
 *  Deliberately vague at the edges: "1.2 months" invites arithmetic nobody
 *  wants in a demo, while "under 2 months" is the same fact and survives being
 *  repeated to someone who was not in the room. Null when either half is
 *  missing, so the sentence simply leaves the clause out. */
const paybackPhrase = (price, savedPerYear) => {
    const saved = Number(savedPerYear) || 0;
    if (!price || saved <= 0) return null;
    const months = (price / saved) * 12;
    if (months < 1) return "under a month";
    if (months < 12) {
        const m = Math.ceil(months);
        return `under ${m} month${m > 1 ? "s" : ""}`;
    }
    return `${(months / 12).toFixed(1)} years`;
};

/* The five, in the order they are read. Titles are ours and short — the
   engine's own titles are full sentences, which do not fit a card. */
const MATRIX = [
    { key: "op_no_show",          title: "No-shows",   sub: "Booked slots nobody turns up for" },
    { key: "discharge_delay",     title: "Inpatient",  sub: "Bed-days lost to slow discharge" },
    { key: "diagnostics_leakage", title: "Lab & scan", sub: "Tests done, never billed" },
    { key: "pharmacy_shrinkage",  title: "Pharmacy",   sub: "Stock lost to expiry and untracked issue" },
    { key: "billing_capture",     title: "Billing",    sub: "Chargeable items that never reach the bill" },
];

const FIELDS = [
    { key: "beds",         label: "Beds" },
    { key: "op_per_day",   label: "OP / day" },
    { key: "ip_per_month", label: "IP / month" },
];

export default function QuotationKpi() {
    const { id } = useParams();
    const { fetch } = useApiMethods();

    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState("");
    const [planId, setPlanId]   = useState("");
    // Empty string means "leave it to whatever the quotation and profile say" —
    // not zero.
    const [draft, setDraft]     = useState({ beds: "", op_per_day: "", ip_per_month: "" });
    const [applied, setApplied] = useState({});

    const load = useCallback(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (planId) params.append("plan_id", planId);
        Object.entries(applied).forEach(([k, v]) => {
            if (v !== "" && v != null) params.append(k, v);
        });
        const qs = params.toString();
        fetch(qs ? `${QUOTATION_KPI_URL(id)}?${qs}` : QUOTATION_KPI_URL(id))
            .then(res => {
                if (res?.status === "success") {
                    setData(res);
                    setError("");
                    if (!planId && res.plan?.id) setPlanId(String(res.plan.id));
                } else {
                    setError(res?.message || "Could not load the KPIs");
                }
            })
            .catch(() => setError("Could not load the KPIs"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, planId, applied]);

    useEffect(() => { load(); }, [load]);

    const audit  = data?.audit;
    const totals = audit?.totals;

    /* The five cards, resolved once: a priced leak, or the unpriced entry that
       explains why there is no figure. */
    const cards = useMemo(() => {
        const priced   = Object.fromEntries((audit?.leaks    || []).map(l => [l.key, l]));
        const unpriced = Object.fromEntries((audit?.unpriced || []).map(u => [u.key, u]));
        return MATRIX.map(m => ({ ...m, leak: priced[m.key], gap: unpriced[m.key] }));
    }, [audit]);

    // Prefill the boxes from whatever the backend resolved, so the rep edits a
    // real number rather than guessing what it currently is.
    const resolved = useMemo(() => {
        const out = {};
        (audit?.inputs?.volumes || []).forEach(v => { out[v.key] = v; });
        if (out.op_per_year && audit?.inputs?.op_per_day) {
            out.op_per_day = { ...out.op_per_year, value: audit.inputs.op_per_day };
        }
        return out;
    }, [audit]);

    const apply = () => setApplied(
        Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== "")),
    );

    if (loading && !data) {
        return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">
            Working out the numbers…
        </div>;
    }
    if (error && !data) {
        return <div className="min-h-screen grid place-items-center bg-background">
            <div className="text-center">
                <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
                <p className="text-foreground font-medium">{error}</p>
            </div>
        </div>;
    }

    const priced = totals?.drivers_priced || 0;
    const hospital = data.quotation?.customer_name || "This hospital";
    const planLabel = data.plan?.label || "This plan";
    const price = Number(data.plan?.grand_total) || 0;

    const payback = paybackPhrase(price, totals?.covered);

    return (
        <div className="min-h-screen bg-background text-foreground">
            <div className="max-w-[1600px] mx-auto px-6 py-6 space-y-6">

                {/* ── who this is about, and the numbers it stands on ──────
                    Header and inputs share one strip. On a projector every row
                    that is not a finding is a row of wasted height. */}
                <div className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
                    <div>
                        <h1 className="text-3xl font-semibold leading-tight">{hospital}</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            {[data.quotation?.customer_city,
                              data.quotation?.quote_number,
                              data.product_known ? data.product_label : null,
                             ].filter(Boolean).join(" · ")}
                        </p>
                        {/* The quote amount, on the same line of sight as the
                            hospital's name. Every number below is answering
                            "is this worth it?", and this is the other side of
                            that question. */}
                        {price > 0 && (
                            <p className="text-sm mt-1 tabular-nums">
                                <span className="text-muted-foreground">{planLabel} — </span>
                                <strong className="text-base">{rupees(price)}</strong>
                                <span className="text-muted-foreground">
                                    {" "}incl. GST
                                    {data.plan?.discount_amount > 0 && <>, after {rupees(data.plan.discount_amount)} discount</>}
                                </span>
                            </p>
                        )}
                    </div>

                    <div className="flex flex-wrap items-end gap-3">
                        {FIELDS.map(f => {
                            const src = resolved[f.key];
                            return (
                                <div key={f.key}>
                                    <label className="block text-[11px] text-muted-foreground mb-1">
                                        {f.label}
                                    </label>
                                    <input
                                        type="number" min="0"
                                        value={draft[f.key]}
                                        placeholder={src ? num(src.value) : "—"}
                                        onChange={e => setDraft(d => ({ ...d, [f.key]: e.target.value }))}
                                        onKeyDown={e => e.key === "Enter" && apply()}
                                        className="w-24 h-9 rounded-lg border border-border bg-background
                                                   px-3 text-sm tabular-nums" />
                                </div>
                            );
                        })}
                        <button onClick={apply}
                            className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium">
                            Recalculate
                        </button>
                        {(data.plans || []).length > 1 && (
                            <select value={planId} onChange={e => setPlanId(e.target.value)}
                                className="h-9 rounded-lg border border-border bg-card px-3 text-sm">
                                {data.plans.map(p => (
                                    <option key={p.id} value={p.id}>
                                        {p.grand_total > 0 ? `${p.label} — ${rupees(p.grand_total)}` : p.label}
                                    </option>
                                ))}
                            </select>
                        )}
                        <button onClick={load} title="Refresh"
                            className="h-9 w-9 rounded-lg border border-border bg-card grid place-items-center hover:bg-accent">
                            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                        </button>
                    </div>
                </div>

                {priced === 0 ? (
                    <div className="rounded-xl border border-amber-300 bg-amber-50 dark:bg-amber-900/10 p-6">
                        <p className="font-medium text-amber-900 dark:text-amber-200">
                            Nothing can be priced yet.
                        </p>
                        <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                            {(data.missing_inputs || []).length > 0 && <>
                                Ask the hospital: {data.missing_inputs.join(", ")}.{" "}
                            </>}
                            {(data.missing_settings || []).length > 0 && <>
                                No rate is built into this page —{" "}
                                <a href="/settings/leak-assumptions" target="_blank" rel="noreferrer"
                                   className="underline font-medium">
                                    set {data.missing_settings.length} figures in Settings → Leak assumptions
                                </a>.
                            </>}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* ── the five ─────────────────────────────────────
                            Three questions per card, always in the same order,
                            so the eye reads down a column across all five:
                            what it earns, what leaks, what we get back. */}
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                            {cards.map(c => {
                                const l = c.leak;

                                /* Unpriced keeps its card. A missing figure is a
                                   question to ask, and it belongs in front of the
                                   customer as much as any number does. */
                                if (!l) {
                                    const needs = [
                                        ...(c.gap?.missing_labels || []),
                                        ...(c.gap?.missing_setting_labels || []),
                                    ];
                                    return (
                                        <div key={c.key}
                                            className="rounded-2xl border border-dashed border-border bg-muted/30 p-5 flex flex-col">
                                            <p className="text-2xl font-semibold text-muted-foreground">{c.title}</p>
                                            <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>
                                            <p className="text-sm text-muted-foreground mt-auto pt-6">
                                                {needs.length
                                                    ? <>Not counted yet — needs {needs.join(", ")}.</>
                                                    : <>Not counted for this hospital.</>}
                                            </p>
                                        </div>
                                    );
                                }

                                return (
                                    <div key={c.key}
                                        className={cn(
                                            "rounded-2xl border bg-card p-5 flex flex-col",
                                            l.is_plugged ? "border-emerald-500/60" : "border-border",
                                        )}>
                                        <p className="text-2xl font-semibold leading-tight">{c.title}</p>
                                        <p className="text-xs text-muted-foreground mt-1">{c.sub}</p>

                                        {/* 1. what this part of the hospital earns today */}
                                        <div className="mt-5">
                                            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                                                Earning today
                                            </p>
                                            <p className="text-xl font-medium tabular-nums mt-0.5">
                                                {money(l.basis_metric?.value)}
                                                <span className="text-xs font-normal text-muted-foreground"> a year</span>
                                            </p>
                                        </div>

                                        {/* 2. how much of it never arrives */}
                                        <div className="mt-4 pt-4 border-t border-border">
                                            <p className="text-[11px] uppercase tracking-wider text-rose-600 dark:text-rose-400">
                                                Missing today
                                            </p>
                                            <p className="text-4xl font-semibold tabular-nums text-rose-600 dark:text-rose-400 mt-0.5">
                                                {money(l.annual_leak)}
                                            </p>
                                            <p className="text-[11px] text-muted-foreground mt-0.5">{l.rate_label}</p>
                                        </div>

                                        {/* 3. what eMedHub saves, and how */}
                                        <div className="mt-4 pt-4 border-t border-border flex-1 flex flex-col">
                                            <p className="text-[11px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                                                eMedHub saves you
                                            </p>
                                            <p className="text-4xl font-semibold tabular-nums text-emerald-700 dark:text-emerald-300 mt-0.5">
                                                {money(l.recoverable)}
                                            </p>
                                            <p className="text-sm mt-2">{l.fix}</p>
                                            <p className="mt-auto pt-3">
                                                {l.is_plugged ? (
                                                    <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md
                                                                     bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
                                                        <Check className="h-3 w-3 shrink-0" />
                                                        {l.plugged_by.join(", ")}
                                                    </span>
                                                ) : (
                                                    <span className="text-[11px] px-2 py-1 rounded-md bg-muted text-muted-foreground">
                                                        not on {data.plan?.label || "this plan"}
                                                    </span>
                                                )}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* ── the single line ──────────────────────────────
                            Everything above in one sentence, because this is
                            the sentence that gets repeated to whoever was not
                            in the room. The price belongs in it: "we recover
                            ₹7.5 L a year" and "it costs ₹73,744" are the two
                            halves of the only question being asked. */}
                        <div className="rounded-2xl border-2 border-primary/40 bg-primary/5 px-6 py-5">
                            <p className="text-xl sm:text-2xl leading-relaxed">
                                <strong className="tabular-nums">{money(totals.annual_leak)}</strong>{" "}
                                a year is going missing at {hospital}.{" "}
                                <strong>{planLabel}</strong> saves{" "}
                                <strong className="text-emerald-700 dark:text-emerald-300 tabular-nums">
                                    {money(totals.covered)}
                                </strong>{" "}
                                of it a year
                                {price > 0 && <>
                                    {" "}and costs{" "}
                                    <strong className="tabular-nums">{rupees(price)}</strong>
                                    {payback && <> — paid back in <strong>{payback}</strong></>}
                                </>}.
                                {totals.not_covered > 0 && <>
                                    {" "}The other{" "}
                                    <strong className="tabular-nums">{money(totals.not_covered)}</strong>{" "}
                                    keeps leaking until the rest is covered.
                                </>}
                            </p>
                            {/* Stated, because a price that excludes the recurring
                                part reads as the whole cost when it is not. */}
                            {price > 0 && (
                                <p className="text-[12px] text-muted-foreground mt-2 tabular-nums">
                                    {rupees(data.plan?.net_amount)} + GST {rupees(data.plan?.gst_amount)}
                                    {data.plan?.amc_rate > 0 && <>, then AMC {data.plan.amc_rate}% a year from year two</>}
                                </p>
                            )}
                        </div>
                    </>
                )}

                {/* One line, closed. The arithmetic has to be reachable when a
                    CFO asks "says who?", and invisible until they do. */}
                <details className="text-[12px] text-muted-foreground">
                    <summary className="cursor-pointer">How every number was worked out</summary>
                    <div className="mt-2 space-y-1">
                        {(audit?.leaks || []).map(l => (
                            <div key={l.key} className="tabular-nums">
                                <span className="font-medium text-foreground">{l.title}: </span>
                                {l.rate_label} of {money(l.basis_metric?.value)} = {money(l.annual_leak)} a year,
                                of which {l.recoverable_pct}% is counted recoverable = {money(l.recoverable)}
                            </div>
                        ))}
                        <p className="pt-2">
                            Every rate and bill value is one this organisation configured in
                            Settings → Leak assumptions; nothing here falls back to a default.
                            {(audit?.inputs?.volumes || []).filter(v => v.note).length > 0 && <>
                                {" "}{(audit.inputs.volumes || []).filter(v => v.note)
                                        .map(v => `${v.label}: ${v.note}`).join(" · ")}.
                            </>}
                            {(audit?.inputs?.facilities_inferred || []).length > 0 && <>
                                {" "}Assumed from the modules quoted, not confirmed in discovery:{" "}
                                {audit.inputs.facilities_inferred.join(", ")}.
                            </>}
                        </p>
                    </div>
                </details>
            </div>
        </div>
    );
}
